/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-31d (22/10/2026) — dữ liệu đèn NEMA của MỘT tủ cho web: hook + kiểu + nhãn badge.
 *
 * Nguồn: `GET /api/nema/tu/:id` (máy chủ tính badge bằng ĐÚNG hàm của luật — web chỉ vẽ).
 * 🔴 LUẬT SỐ 0: số NULL vẽ "—". Không có chỗ nào ở đây đổi NULL thành 0.
 * Quy tắc giao diện (lee 22/10): nhãn ngắn, không câu diễn giải, không thông tin nội bộ thiết bị.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TienTrinhTu } from './tienTrinhLenh';
import { ApiError, api } from './api';

import type { BadgeDen, LyDoKhongNhan, ToneBadge } from './nemaNhan';
/* Nhãn / màu / tên đèn sống ở `lib/nemaNhan.ts` (thuần — bộ kiểm ghim); xuất lại cho chỗ dùng cũ. */
export { NHAN_BADGE, MAU_BADGE, tenDen, chuaDatTen, type BadgeDen, type ToneBadge } from './nemaNhan';
export type TrangThaiLenh = 'CHO_GIAO_THUC' | 'CHO_TU' | 'DA_GUI' | 'ACK_OK' | 'ACK_LOI' | 'HET_HAN';
export type KindLenh = 'dim' | 'quet_dien' | 'quet_gps' | 'dung_quet' | 'lich' | 'tra_ve_lich' | 'nhan_den' | 'nhay' | 'lich_dim' | 'gan_nhom';

export interface DenNema {
  addr: number; stt: number | null; name: string | null; rated_w: number | null; pole_no: string | null; groupId: string | null; note: string | null;
  /** TK-39 §4: số nhóm card ĐÃ XÁC NHẬN (ack gan_nhom status 0); null = chưa biết */
  nhomThietBi: number | null;
  online: boolean | null; last_seen_at: string | null;
  badge: BadgeDen; giaLap: boolean | null;
  /** TK-35: nhánh = ngõ ra cấp điện; capDien null = không biết (tủ mất kết nối) */
  nhanh: number | null; capDien: boolean | null;
  u_v: number | null; i_10ma: number | null; p_w: number | null; pf_pct: number | null; dim: number | null; relay: number | null; sensor_at: string | null;
  /** TK-44: máy chủ tính (`nemaDen`) — số đo NULL khi không hiện hành; lệnh chỉ bấm được khi `nhanLenh` */
  soDoHienHanh: boolean; nhanLenh: boolean; lyDo: LyDoKhongNhan | null;
  gps: { result: number | null; flags: number | null; lat_e7: number | null; lon_e7: number | null; at: string | null };
  pos: { lat: number; lng: number; src: string | null; at: string | null; by: string | null } | null;
  /** KA-49 §4.2: 0 đo thật · 1 giả lập (NULL = card chưa báo) · bộ đếm khung · iid 8 byte hex */
  src?: number | null; khung?: { ok: number; loi: number } | null; iid?: string | null;
}
export interface HostNema {
  /** KA-49 §4.1: kênh RF card báo (NULL = chưa biết) */
  rf_ch?: number | null;
  state: number | null; demo: boolean | null; node_online: number | null; node_seen: number | null; status_at: string | null;
  last_sensor_s: number | null; last_gps_s: number | null;
  sweep_kind: number | null; sweep_state: number | null; sweep_total: number | null; sweep_done: number | null; sweep_ok: number | null; sweep_no_reply: number | null; sweep_eta_s: number | null; sweep_at: string | null;
  giaLap: boolean | null; matLienLac: boolean | null;
  /** TK-35: thông số "tủ đã nhận đèn" (chỉ khung chi tiết kỹ thuật) */
  rf_channel: number | null; pan_id: number | null; so_node_khai: number | null; nhan_luc: string | null;
}
export interface LenhNema {
  id: string; cabinetId: string; kind: KindLenh; addrs: number[]; level: number | null; groupId: string | null;
  state: TrangThaiLenh; issuedById: string | null; issuedAt: string; sentAt: string | null; ackAt: string | null;
  ack: { result: string; detail: string | null; perLamp: { addr: number; status: number }[] | null } | null;
  xacNhan: { dung: number; tong: number; luc: string } | null;
}
export interface VanDeGiaLap { id: string; addr: number | null; code: string; mucDo: number; message: string; raisedAt: string }
export interface TuNema {
  cabinet: { id: string; name: string; hardwareId: string; routeId: string | null; online: boolean; giaLap: boolean; numOutputs: number; hostBat: boolean | null;
    matTroi: { sunsetMin: number | null; sunriseMin: number | null } };
  host: HostNema | null;
  den: DenNema[];
  tom: { tong: number; online: number; capDien: number; sang: number };
  lenh: LenhNema[];
  vanDeGiaLap: VanDeGiaLap[];
  /** TK-35: ngõ ra 1..n của tủ (nguồn điện của đèn); null = tủ mất kết nối / chưa có ngõ ra (KHÔNG biết, không phải "tắt") */
  ngoRa: { on: boolean; tu: string | null; tay: boolean }[] | null;
  /** TK-44: tủ nhận "Trả về lịch" được không (tủ trực tuyến + mesh bật) — null = được */
  lyDoTraVe: LyDoKhongNhan | null;
}
/** TK-35 lượt 2: nhóm dim thuộc TỦ. */
/** TK-39 §4: `so` = số nhóm ở thiết bị 1..254 (hiện "tên (số)"). */
export interface NhomNema { id: string; name: string; color: string | null; cabinetId: string | null; so: number | null; soDen: number; soTu: number; den: { cabinetId: string; addr: number }[] }
/** TK-35 lượt 2: một hàng của trang "Đèn NEMA" (tủ có đèn). */
export interface TuCoDen {
  id: string; name: string; code: string | null; online: boolean; giaLap: boolean; daNhan: boolean;
  tuyen: { id: string; tenDayDu: string } | null;
  soDen: number; sang: number; capDien: number; chuaXep: number; vanDe: { that: number; giaLap: number };
}

export const qkNema = {
  tu: (id: string) => ['nema', 'tu', id] as const,
  h24: (id: string, addr: number) => ['nema', 'tu', id, '24h', addr] as const,
  nhom: (id: string) => ['nema', 'tu', id, 'nhom'] as const,
  lich: (id: string) => ['nema', 'tu', id, 'lich'] as const,
  tuCoDen: ['nema', 'tu-co-den'] as const,
};

export function useNemaTu(id: string | undefined) {
  return useQuery({
    queryKey: qkNema.tu(id ?? ''),
    queryFn: () => api.get<TuNema>(`/api/nema/tu/${id}`),
    enabled: !!id,
    refetchInterval: 5_000,
  });
}
export function useNema24h(id: string | undefined, addr: number | null) {
  return useQuery({
    queryKey: qkNema.h24(id ?? '', addr ?? 0),
    queryFn: () => api.get<{ tu: string; den: string; rows: { ts: string; u_v: number | null; p_w: number | null; dim: number | null }[] }>(`/api/nema/tu/${id}/den/${addr}/24h`),
    enabled: !!id && addr !== null,
    refetchInterval: 30_000,
  });
}
export function useGuiLenhNema(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { kind: KindLenh; addrs?: number[]; level?: number; lich?: unknown; groupId?: string }) => api.post<LenhNema>(`/api/nema/tu/${id}/lenh`, b),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qkNema.tu(id) }); },
  });
}
export function useSuaDenNema(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { addr: number; name?: string; rated_w?: number | null; pole_no?: string | null; groupId?: string | null; nhanh?: number | null }) =>
      api.put(`/api/nema/tu/${id}/den/${v.addr}`, { name: v.name, rated_w: v.rated_w, pole_no: v.pole_no, groupId: v.groupId, nhanh: v.nhanh }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qkNema.tu(id) }); },
  });
}
/* ───────────── TK-35 lượt 2: nhóm dim theo tủ · trang tủ có đèn ───────────── */
export function useNhomTu(id: string | undefined) {
  return useQuery({ queryKey: qkNema.nhom(id ?? ''), queryFn: () => api.get<NhomNema[]>(`/api/nema/tu/${id}/nhom`), enabled: !!id, refetchInterval: 10_000 });
}
export function useSuaNhomTu(id: string) {
  const qc = useQueryClient();
  const lamMoi = () => { void qc.invalidateQueries({ queryKey: qkNema.tu(id) }); };
  const tao = useMutation({ mutationFn: (b: { name: string; color?: string }) => api.post<NhomNema>(`/api/nema/tu/${id}/nhom`, b), onSuccess: lamMoi });
  /* TK-39 §4: nhóm đang có timer dim dùng ⇒ máy chủ 409 { soTimer } ⇒ hỏi lại đúng câu phiếu, đồng ý ⇒ ?xacNhan=1 (timer ⇒ 255).
     Trả `false` khi người dùng không đồng ý (không phải lỗi). */
  const xoa = useMutation({
    mutationFn: async (nhomId: string): Promise<boolean> => {
      try { await api.del(`/api/nema/nhom/${nhomId}`); return true; } catch (e) {
        const n = e instanceof ApiError && e.status === 409 ? Number(e.body?.soTimer) : NaN;
        if (!Number.isInteger(n) || n <= 0) throw e;
        if (!window.confirm(`${n} timer đang dùng nhóm này sẽ chuyển thành không dùng`)) return false;
        await api.del(`/api/nema/nhom/${nhomId}?xacNhan=1`); return true;
      }
    },
    onSuccess: lamMoi,
  });
  const thanhVien = useMutation({ mutationFn: (v: { nhomId: string; addrs: number[] }) => api.put(`/api/nema/nhom/${v.nhomId}/den`, { den: v.addrs.map((addr) => ({ cabinetId: id, addr })) }), onSuccess: lamMoi });
  return { tao, xoa, thanhVien };
}
/* ───────────── TK-38 việc 4: cây Tuyến → Tủ → Đèn (trang Quản lý đèn) + lịch sử một đèn ───────────── */
export interface DenCay { addr: number; stt: number | null; name: string | null; badge: BadgeDen; capDien: boolean | null; p_w: number | null; online: boolean | null }
export interface TuCay {
  id: string; name: string; code: string | null; online: boolean; giaLap: boolean;
  /** tủ đang nối từ lúc (ISO); null = không nối */
  lucNoi: string | null; lastSeenAt: string | null; lastRebootAt: string | null;
  host: { state: number | null; statusAt: string | null; rf_channel: number | null; pan_id: number | null } | null;
  soDen: number; sang: number; capDien: number;
  /** tổng P các đèn có số (đèn chưa cấp điện không tính); null = không đèn nào có số */
  congSuatW: number | null;
  vanDe: { that: number; giaLap: number };
  den: DenCay[];
  /** TK-44: tủ chưa có bảng đèn đã lưu ⇒ chip "Chưa cài", không có cấp con */
  chuaCai: boolean;
}
export interface TuyenCay { id: string; tenDayDu: string; tu: TuCay[] }
export function useCayDen() {
  return useQuery({ queryKey: ['nema', 'cay'] as const, queryFn: () => api.get<TuyenCay[]>('/api/nema/cay'), refetchInterval: 10_000 });
}
export type KhoangLichSu = '24h' | '7d';
export interface MauLichSu { ts: string; u_v: number; i_10ma: number; p_w: number; pf_pct: number; dim: number | null }
export function useLichSuDen(cabinetId: string | undefined, addr: number | null, khoang: KhoangLichSu) {
  return useQuery({
    queryKey: ['nema', 'lich-su', cabinetId ?? '', addr ?? 0, khoang] as const,
    queryFn: () => api.get<{ tu: string; den: string; khoang: KhoangLichSu; rows: MauLichSu[] }>(`/api/nema/tu/${cabinetId}/den/${addr}/lich-su?khoang=${khoang}`),
    enabled: !!cabinetId && addr !== null, refetchInterval: 60_000,
  });
}

export function useTuCoDen() {
  return useQuery({ queryKey: qkNema.tuCoDen, queryFn: () => api.get<TuCoDen[]>('/api/nema/tu-co-den'), refetchInterval: 10_000 });
}

/* ───────────── nhãn — ngắn, theo quy tắc giao diện 22/10 ───────────── */

export const NHAN_LENH: Record<KindLenh, string> = {
  dim: 'Dim', quet_dien: 'Quét', quet_gps: 'Quét GPS', dung_quet: 'Dừng quét', lich: 'Lịch dim', tra_ve_lich: 'Về lịch', nhan_den: 'Nhận đèn', nhay: 'Nháy', lich_dim: '32 timer dim', gan_nhom: 'Gán nhóm',
};
export const NHAN_TRANG_THAI_LENH: Record<TrangThaiLenh, { nhan: string; tone: ToneBadge }> = {
  CHO_GIAO_THUC: { nhan: 'Chờ giao thức', tone: 'slate' },
  CHO_TU: { nhan: 'Đang gửi', tone: 'blue' },
  DA_GUI: { nhan: 'Chờ tủ', tone: 'blue' },
  ACK_OK: { nhan: 'Tủ đã nhận', tone: 'green' },
  ACK_LOI: { nhan: 'Tủ từ chối', tone: 'red' },
  HET_HAN: { nhan: 'Không hồi đáp', tone: 'red' },
};
/** Bảng tên việc ngắn sống ở `lib/vande.ts` (thuần — bộ kiểm nhập được); xuất lại ở đây cho chỗ dùng cũ. */
export { VIEC_NGAN } from './vande';
/** Câu lệnh ngắn cho nhật ký: "Dim Trụ 03 · 0 %", "Quét", "Dim tất cả · 100 %". */
export function cauLenh(l: LenhNema, ten: (addr: number) => string): string {
  const dich = l.addrs.length === 0 ? 'tất cả' : l.addrs.length === 1 ? ten(l.addrs[0]) : `${l.addrs.length} đèn`;
  if (l.kind === 'dim') return `Dim ${dich} · ${l.level ?? '—'} %`;
  if (l.kind === 'tra_ve_lich') return `Về lịch ${l.addrs.length ? dich : ''}`.trim();
  return NHAN_LENH[l.kind];
}
/** Kết quả hiện ở cột cuối: xác nhận bằng quét nếu có, không thì trạng thái lệnh. */
export function ketQuaLenh(l: LenhNema): { nhan: string; tone: ToneBadge } {
  if (l.kind === 'dim' && l.xacNhan) return l.xacNhan.dung === l.xacNhan.tong ? { nhan: 'Đúng', tone: 'green' } : { nhan: `${l.xacNhan.dung}/${l.xacNhan.tong} đúng`, tone: 'amber' };
  if (l.kind === 'quet_dien' && l.state === 'ACK_OK' && l.ack?.perLamp === null) return { nhan: 'Xong', tone: 'green' };
  return NHAN_TRANG_THAI_LENH[l.state];
}
/** Chuỗi "x phút" / "HH:MM" cho cột Cập nhật — ngắn. */
export function tuoiNgan(iso: string | null, now = Date.now()): string {
  if (!iso) return '—';
  const ms = now - new Date(iso).getTime();
  if (ms < 60_000) return 'vừa xong';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} phút`;
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/* ───────────── KA-49 §4.3: dữ liệu thô tiến trình lệnh (mỗi tủ một truy vấn; luật 4 bước ở `lib/tienTrinhLenh.ts`) ───────────── */
/**
 * Map cabinetId → dữ liệu; tủ CHƯA tải xong ⇒ vắng khỏi map (luật số 0: chỗ dùng vẽ "—", không coi là "không có lệnh").
 * Khoá dưới `['nema','tu',id]` ⇒ gửi lệnh (invalidate `['nema']`) làm mới ngay.
 */
export function useTienTrinhCacTu(ids: readonly string[]): Map<string, TienTrinhTu> {
  const qs = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['nema', 'tu', id, 'tien-trinh'] as const,
      queryFn: () => api.get<TienTrinhTu>(`/api/nema/tu/${id}/tien-trinh`),
      refetchInterval: 4_000,
    })),
  });
  const m = new Map<string, TienTrinhTu>();
  qs.forEach((q, i) => { if (q.data) m.set(ids[i], q.data); });
  return m;
}
